var _excluded = ["icon", "title", "subtitle", "shape", "onClick", "utils", "actionIcon", "hasActionIcon", "onContainerSize", "iconAlt", "actionIconAlt", "borderLeft", "isVertical", "disabled", "isInverse", "size"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Icon from '../icon';
import Type from '../type';
import Tile from '../tile';
import StyledButton from '../button/button-new.styles';
import StyledSlabIcon from './styles/slab-icon.styles';
import StyledSlabContent from './styles/slab-content.styles';
import StyledSlabText from './styles/slab-content.text.styles';
import StyledSlab from './slab.styles';
import ContainerQuery from '../container-query';
import InverseContext from '../../utilities/inverse-context';
import { StyledCacheWrapper } from '../../styles';
import withDeprecatedProps from '../../utilities/with-deprecated-props/withDeprecatedProps';
var slabPropsTypes = {
  /** Icons from icon library*/
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Adds a border to the left of the slab. (See Tile). */
  borderLeft: PropTypes.oneOf(['default', 'indigo', 'navy', 'blue', 'green', 'coral', 'orange', 'red', 'violet', 'yellow', 'gray']),
  /** Slab title */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Slab subtitle */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * @deprecated since 2.54.0
   * @see title
   */
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Slab shape */
  shape: PropTypes.string,
  /** Slab onClick event */
  onClick: PropTypes.func,
  /** Add utility objects for styling */
  utils: PropTypes.object,
  /** Callback for when component resizes */
  onContainerSize: PropTypes.func,
  /** Action icon from icon library*/
  actionIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Toggle the action icon */
  hasActionIcon: PropTypes.bool,
  /**
   * @deprecated since 2.62.0
   * @see hasActionIcon
   */
  hasArrow: PropTypes.bool,
  /** Alternative text used to set the icon SVG title */
  iconAlt: PropTypes.string,
  /** Alternative text used to set the action icon SVG title */
  actionIconAlt: PropTypes.string,
  /** Slab has vertical layout */
  isVertical: PropTypes.bool,
  /** Disable the slab */
  disabled: PropTypes.bool,
  /** Component design with dark background. */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** Making the Slab message with small or medium */
  size: PropTypes.oneOf(['sm', 'md'])
};
var slabPropsDefault = {
  icon: '',
  title: '',
  subtitle: '',
  text: null,
  shape: 'square',
  utils: {},
  actionIcon: 'arrowRight',
  hasActionIcon: true,
  hasArrow: null,
  onClick: function onClick() {},
  onContainerSize: function onContainerSize() {},
  iconAlt: '',
  actionIconAlt: 'Right arrow',
  borderLeft: 'green',
  isVertical: false,
  disabled: false,
  isInverse: 'auto',
  size: 'md'
};

/**
 * @ignore @description
 * ## Slab Component
 * Use the Slab component to create equally sized interactive containers that house icons and text
 *
 * @ignore @see Mesh: [Slab Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/actions-slab--default)
 *
 * @ignore @example
 * ```jsx
 * import { Slab } from 'mesh-component-library';
 *
 * <Slab {...props}/>
 * ```
 * @type React.ForwardRefRenderFunction<React.FunctionComponent, propTypes>
 */

export var Slab = /*#__PURE__*/React.forwardRef(function (props, forwardedRef) {
  var icon = props.icon,
    title = props.title,
    subtitle = props.subtitle,
    shape = props.shape,
    onClick = props.onClick,
    utils = props.utils,
    actionIcon = props.actionIcon,
    hasActionIcon = props.hasActionIcon,
    onContainerSize = props.onContainerSize,
    iconAlt = props.iconAlt,
    actionIconAlt = props.actionIconAlt,
    borderLeft = props.borderLeft,
    isVertical = props.isVertical,
    disabled = props.disabled,
    isInverse = props.isInverse,
    size = props.size,
    other = _objectWithoutProperties(props, _excluded);
  var useIsInverse = useContext(InverseContext);
  if (isInverse !== 'auto') useIsInverse = isInverse;
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(ContainerQuery, {
    onContainerSize: onContainerSize
  }, /*#__PURE__*/React.createElement(StyledSlab, _extends({
    utils: utils,
    ref: forwardedRef,
    borderLeft: borderLeft,
    size: size
  }, other, {
    isInverse: useIsInverse,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Tile, {
    borderLeft: borderLeft,
    isInverse: useIsInverse
  }, /*#__PURE__*/React.createElement(StyledButton, {
    isHollow: true,
    isPrimary: true,
    shape: shape,
    onClick: onClick,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(StyledSlabContent, {
    isVertical: isVertical
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: icon,
    size: 3,
    color: "coolGray-800",
    alt: iconAlt
  }), /*#__PURE__*/React.createElement(StyledSlabText, null, /*#__PURE__*/React.createElement(Type, {
    size: size === 'sm' ? 'l' : '2xl',
    variant: useIsInverse ? 'default' : 'dark',
    lineHeight: "lg",
    weight: "semibold"
  }, title), /*#__PURE__*/React.createElement(Type, {
    element: "span",
    size: size === 'sm' ? 'xs' : 's',
    variant: useIsInverse ? 'default' : 'dark'
  }, subtitle))), hasActionIcon && /*#__PURE__*/React.createElement(StyledSlabIcon, null, /*#__PURE__*/React.createElement(Icon, {
    icon: actionIcon,
    size: 1,
    alt: actionIconAlt
  })))))));
});
Slab.propTypes = slabPropsTypes;
Slab.displayName = 'Slab';
Slab.defaultProps = slabPropsDefault;

/**
 * @type React.FC<slabPropsTypes>
 */
export default withDeprecatedProps(Slab);